// #4981 phase 3 (#5269) — the one rule for turning a stored config document into
// the value THIS user gets.
//
// This module imports NOTHING, deliberately, so a plain-node test can load it and
// so the migration can use it. `local-database-engine-decisions.ts` is the pattern.
//
// **Why it is here rather than exported from `config-db.service.ts`.** That file
// imports `@angular/core`, `AuthService`, `CommonDbService` and `LoggingService`,
// and through them reaches `pouchdb-browser`, which references `self` and does not
// load under plain node at all. The #4981 phase 3 migration is Angular-free and
// node-tested, so it could never import that file. Rather than let it keep a second
// copy of the resolution rule — which would then drift, silently, on a rule that
// decides whether a rollout control is obeyed — both call this.

/**
 * A config document as it is stored: one value for everybody, plus per-user
 * overrides.
 *
 * Structural rather than a class, so a caller holding a plain object read straight
 * out of PouchDB can pass it without constructing anything.
 */
export interface ConfigRecordShape<Type> {
  /** the value for every user with no override below */
  globalValue: Type;
  /** per-user overrides, keyed by user id */
  userValues?: { [userId: string]: Type };
}

/**
 * What value does this config document give THIS user?
 *
 * A per-user override wins when the key is **PRESENT**, whatever it holds —
 * including `null`. Otherwise the global value.
 *
 * **The presence test is `!== undefined`, and writing it as `??` would be a real
 * behaviour change rather than a tidier spelling.** The two differ on exactly one
 * case: an override explicitly set to `null`. Under `!== undefined` that override
 * is honoured and the user gets `null`; under `??` it is skipped and the user
 * silently gets the global value instead. Because `ConfigDbService` resolves EVERY
 * config key in the app through this function, that difference would not be
 * confined to the migration keys — it would change how an operator's deliberate
 * `null` override behaves on any key, in a place nobody would think to look.
 *
 * A missing `userValues` map is treated as no overrides, so a hand-written or
 * older-shaped document resolves rather than throwing.
 */
export function resolveConfigRecordForUser<Type>(
  record: ConfigRecordShape<Type> | null | undefined,
  userId: string | undefined,
): Type | undefined {
  if (!record) return undefined;
  if (userId && record.userValues && record.userValues[userId] !== undefined) {
    return record.userValues[userId];
  }
  return record.globalValue;
}

/**
 * Read a usable NUMBER out of a value that came from a config document, or
 * `undefined` when there is not one.
 *
 * A numeric STRING is accepted, because the config stores raw JSON and `"10"` is a
 * likely hand-edit. Everything else is refused **BEFORE** the conversion, and that
 * order is the whole point of the function rather than a detail of it: `Number(true)`
 * is 1, so a document holding `true` would convert to a usable-looking 1 and arm
 * whatever rollout control read it. A blank or whitespace-only string is refused for
 * the same reason — `Number('')` is 0, and `Number('   ')` is 0 too.
 *
 * `NaN` and both infinities are refused after the conversion, so a string like
 * `"ten"` cannot travel any further.
 *
 * **This function deliberately applies NO range rule.** Whether a number is too
 * small, too large or negative depends on what it configures, and each caller keeps
 * that rule of its own — {@link readUsableRolloutPercent} for a percentage, a floor
 * for a generation counter. Adding one here would silently change every caller.
 *
 * Every caller reads this the same way round: `undefined` means "unusable, so fail to
 * whatever is safe here", never "use a default".
 */
export function readUsableConfigNumber(value: unknown): number | undefined {
  const isUsableShape = typeof value === 'number'
    || (typeof value === 'string' && value.trim() !== '');
  if (!isUsableShape) return undefined;

  const asNumber = Number(value);
  if (!Number.isFinite(asNumber)) return undefined;
  return asNumber;
}

/**
 * Read a usable ROLLOUT PERCENTAGE out of a config value, or `undefined` when there
 * is not one.
 *
 * {@link readUsableConfigNumber} plus the one range rule every rollout ramp in the app
 * shares: a percentage must be **above 0 and at most 100**.
 *
 * **0 is refused rather than honoured**, and that is not an off-by-one. Every ramp
 * tests `bucketFor(...) < percent` with a bucket in `0..99`, so a percentage of 0
 * selects nobody anyway — refusing it here means a ramp that is switched off and a
 * ramp whose dial is unreadable take the same code path, which is the fail-to-disabled
 * behaviour each of those functions promises in its own doc comment.
 *
 * A caller turns `undefined` into its own "feature off" answer; this function never
 * substitutes a default, because a default percentage would be a rollout nobody asked
 * for.
 */
export function readUsableRolloutPercent(value: unknown): number | undefined {
  const asNumber = readUsableConfigNumber(value);
  if (asNumber === undefined) return undefined;
  if (asNumber <= 0 || asNumber > 100) return undefined;
  return asNumber;
}

/**
 * The users switched on for a key that is set one user at a time
 * (`USER_LIST_CONFIG_KEYS` in config-keys.ts): exactly the per-user entries that are
 * `true`, since nothing else switches a user on. Admin → Config lists these and saves
 * exactly these, so a leftover value of any other shape is dropped on save. A damaged
 * `userValues` (missing, an array, not an object) gives no users.
 */
export function switchedOnUsers(userValues: unknown): { [userId: string]: true } {
  const switchedOn: { [userId: string]: true } = {};
  if (!userValues || typeof userValues !== 'object' || Array.isArray(userValues)) return switchedOn;
  for (const [userId, value] of Object.entries(userValues)) {
    if (value === true) switchedOn[userId] = true;
  }
  return switchedOn;
}
